import { useState } from 'react';
import { ArrowRight, CheckCircle, Phone, Mail, MapPin, Lock, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API, scrollTo } from './moana-data';
import { Eyebrow } from './MoanaHero';

export const MoanaContact = () => {
  const [contactForm, setContactForm] = useState({ name: '', company: '', phone: '', email: '', effectif: '', pack: '', message: '' });
  const [contactSuccess, setContactSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactForm.name || !contactForm.email || !contactForm.phone) {
      toast.error('Veuillez remplir les champs obligatoires'); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: contactForm.name, email: contactForm.email, phone: contactForm.phone,
        company: contactForm.company, subject: 'Moana Paye - Audit gratuit',
        message: `Effectif: ${contactForm.effectif}\nPack envisage: ${contactForm.pack}\n\n${contactForm.message}`
      });
      setContactSuccess(true);
      toast.success('Demande envoyée !');
    } catch { toast.error("Erreur lors de l'envoi."); }
    finally { setSubmitting(false); }
  };

  return (
    <>
      {/* Contact */}
      <section id="contact-moana" className="py-20 bg-gradient-to-br from-[#0F766E] to-[#0D9488]" data-testid="contact-moana">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10">
          <Eyebrow light>Nous contacter</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-12">
            Demandez votre <em className="italic text-[#F59E0B]">audit gratuit</em>
          </h2>
          <div className="grid lg:grid-cols-[5fr_7fr] gap-16">
            {/* Left */}
            <div>
              <p className="text-sm text-white/60 leading-relaxed mb-7">
                Un échange de 20 minutes suffit à clarifier votre situation et à identifier la configuration Moana Paye la plus adaptée. Sans engagement, sans frais.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { icon: Phone, lbl: 'Par téléphone', val: '+689 87 73 78 15', sub: 'Du lundi au vendredi, 8h-17h', href: 'tel:+68987737815' },
                  { icon: Mail, lbl: 'Par email', val: 'contact@groupeaerhpolynesie.com', sub: 'Réponse sous 24 h ouvrées', href: 'mailto:contact@groupeaerhpolynesie.com' },
                  { icon: MapPin, lbl: 'En cabinet', val: 'Papeete, Tahiti · Polynésie française', sub: '10 Rue Georges LAGARDE, 98714' },
                ].map(c => (
                  <div key={c.lbl} className="flex gap-4 items-start bg-white/[0.05] border border-white/[0.08] rounded-lg p-4 hover:border-[#F59E0B]/30 transition-colors">
                    <div className="w-10 h-10 bg-[#F59E0B]/12 rounded-lg flex items-center justify-center flex-shrink-0">
                      <c.icon className="w-4.5 h-4.5 text-[#F59E0B]" />
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold tracking-[0.14em] uppercase text-white/35 mb-0.5">{c.lbl}</p>
                      {c.href ? (
                        <a href={c.href} className="text-sm font-medium text-white hover:text-[#F59E0B] transition-colors">{c.val}</a>
                      ) : (
                        <p className="text-sm font-medium text-white">{c.val}</p>
                      )}
                      <p className="text-xs text-white/40 mt-0.5">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/40 italic leading-relaxed pt-5 border-t border-white/[0.07]">
                "Avant d'agir, un échange permet souvent de clarifier les enjeux et d'identifier l'approche la plus pertinente."
                <br />— Groupe AERH Polynésie
              </p>
            </div>
            {/* Right — Form */}
            <div className="bg-white/[0.04] border border-white/10 rounded-xl p-10">
              {!contactSuccess ? (
                <>
                  <h3 className="text-lg font-bold text-white mb-1">Demande d'audit gratuit</h3>
                  <p className="text-xs text-white/[0.38] mb-7 leading-relaxed">Remplissez ce formulaire — nous vous recontactons sous 24 h pour fixer un créneau.</p>
                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Prénom & Nom *</Label>
                        <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} placeholder="Votre nom" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" required data-testid="contact-name" />
                      </div>
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Entreprise</Label>
                        <Input value={contactForm.company} onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })} placeholder="Votre structure" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" data-testid="contact-company" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Téléphone *</Label>
                        <Input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} placeholder="87 XX XX XX" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" required data-testid="contact-phone" />
                      </div>
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Email *</Label>
                        <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} placeholder="votre@email.com" className="mt-1 bg-white/[0.06] border-white/10 text-white placeholder:text-white/22" required data-testid="contact-email" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Effectif salarié</Label>
                        <Select value={contactForm.effectif} onValueChange={(v) => setContactForm({ ...contactForm, effectif: v })}>
                          <SelectTrigger className="mt-1 bg-white/[0.06] border-white/10 text-white" data-testid="contact-effectif">
                            <SelectValue placeholder="- Sélectionner -" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-5">1 – 5 salariés</SelectItem>
                            <SelectItem value="6-14">6 – 14 salariés</SelectItem>
                            <SelectItem value="15-25">15 – 25 salariés</SelectItem>
                            <SelectItem value="26+">26 salariés et +</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-white/40 text-[10px] uppercase tracking-wider">Pack envisagé</Label>
                        <Select value={contactForm.pack} onValueChange={(v) => setContactForm({ ...contactForm, pack: v })}>
                          <SelectTrigger className="mt-1 bg-white/[0.06] border-white/10 text-white" data-testid="contact-pack">
                            <SelectValue placeholder="- Je ne sais pas encore -" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ne-sais-pas">Je ne sais pas encore</SelectItem>
                            <SelectItem value="essentiel">Pack Essentiel</SelectItem>
                            <SelectItem value="confort">Pack Confort</SelectItem>
                            <SelectItem value="serenite">Pack Serenite</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/40 text-[10px] uppercase tracking-wider">Votre situation actuelle</Label>
                      <textarea value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} rows={3} placeholder="Décrivez votre contexte, vos questions, vos contraintes..."
                        className="mt-1 w-full rounded-md bg-white/[0.06] border border-white/10 text-white placeholder:text-white/22 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#F59E0B] resize-y"
                        data-testid="contact-message" />
                    </div>
                    <Button type="submit" disabled={submitting} className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white font-bold py-5" data-testid="contact-submit">
                      {submitting ? 'Envoi en cours...' : "Envoyer ma demande d'audit"} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                    <p className="text-[11px] text-white/[0.28] text-center flex items-center justify-center gap-1.5">
                      <Lock className="w-3 h-3" /> Vos données ne sont utilisées que pour vous recontacter. Aucun démarchage.
                    </p>
                  </form>
                </>
              ) : (
                <div className="text-center py-8 px-5 bg-green-500/10 border border-green-500/25 rounded-lg">
                  <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Demande envoyée !</h3>
                  <p className="text-sm text-white/60 leading-relaxed">
                    Nous avons bien reçu votre demande d'audit.<br />
                    Vous serez recontacté(e) sous <strong className="text-white">24 h ouvrées</strong> par l'équipe Moana Paye.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Bottom */}
      <section className="bg-[#0F766E] py-16 text-center relative overflow-hidden" data-testid="cta-bottom">
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(74,138,138,.12) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-[640px] mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3.5">
            Externalisez votre paie <em className="italic text-[#F59E0B]">dès le 1er mars 2026.</em>
          </h2>
          <p className="text-sm text-white/55 leading-relaxed mb-8">
            Audit gratuit · Configuration sur mesure · Premier bulletin en 15 jours ouvrés.
          </p>
          <div className="flex flex-wrap justify-center gap-2.5 mb-8">
            {['Réponse sous 24 h', 'Sans engagement', 'Tarifs au 1er mars 2026', 'Papeete, Tahiti'].map(b => (
              <span key={b} className="text-xs font-medium text-white/70 bg-white/[0.07] border border-white/10 rounded-full px-3.5 py-1">{b}</span>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            <Button onClick={() => scrollTo('contact-moana')} className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-7 py-5">
              Demander mon audit gratuit <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button asChild variant="outline" className="border-white/30 text-white hover:border-[#F59E0B] hover:text-[#F59E0B] px-7 py-5">
              <a href="tel:+68987737815"><Phone className="mr-2 w-4 h-4" /> 87 73 78 15</a>
            </Button>
          </div>
        </div>
      </section>

      {/* WhatsApp */}
      <a href="https://api.whatsapp.com/send?phone=68987737815&text=Bonjour%20Moana%20Paye%20!" target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform z-50"
        data-testid="whatsapp-btn" title="Contactez-nous sur WhatsApp">
        <MessageSquare className="w-6 h-6 text-white" />
      </a>
    </>
  );
};
